import { useState } from "react";
import { Globe, Megaphone, BarChart3, Users, ArrowRight, Menu, X, CheckCircle, Phone, Mail, MapPin, TrendingUp, Calendar, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Portfolio data - fictional examples
const portfolioItems = [
{
  id: 1,
  title: "The Crust Bakery",
  category: "Local Business",
  image: "https://images.unsplash.com/photo-1577595927087-dedbe84f0e4d?w=800&q=80",
  description: "Website & Social Media Ads",
  details: "Complete digital presence with online ordering and Instagram campaigns that increased foot traffic by 150%"
},
{
  id: 2,
  title: "City Plumbers",
  category: "Service Provider",
  image: "https://images.unsplash.com/photo-1632496498058-a3bb5998a4f6?w=800&q=80",
  description: "Lead Generation Landing Page",
  details: "High-converting landing page with Google Ads integration generating 40+ qualified leads monthly"
},
{
  id: 3,
  title: "Iron Pulse Gym",
  category: "Fitness",
  image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80",
  description: "Membership Funnel",
  details: "Full membership funnel with Facebook ads targeting local fitness enthusiasts"
},
{
  id: 4,
  title: "Green Thumb Gardens",
  category: "Landscaping",
  image: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&q=80",
  description: "Website & Local SEO",
  details: "Beautiful portfolio website with local SEO optimization for garden services"
}];


// Services data
const services = [
{
  icon: Globe,
  title: "Website Design",
  description: "Custom websites that convert visitors into customers. Mobile-friendly, fast, and professional."
},
{
  icon: Megaphone,
  title: "Social Media Ads",
  description: "Targeted Facebook and Instagram campaigns that reach your ideal local customers."
},
{
  icon: BarChart3,
  title: "Google Ads",
  description: "Get found when customers search for your services. Pay only for results."
},
{
  icon: Users,
  title: "Lead Generation",
  description: "Turn online traffic into real phone calls and appointments for your business."
}];


// Process steps
const processSteps = [
{ step: "01", title: "Discovery Call", description: "We learn about your business, goals, and current challenges." },
{ step: "02", title: "Strategy", description: "We create a custom plan tailored to your market and budget." },
{ step: "03", title: "Build & Launch", description: "We build your digital presence and launch your campaigns." },
{ step: "04", title: "Grow", description: "We optimize and scale what works to grow your business." }];


export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setFormData({
        business_name: "",
        contact_name: "",
        email: "",
        phone: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white font-body" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2" data-testid="logo">
              <span className="font-heading text-2xl md:text-3xl tracking-tight">
                <span className="text-[#D00000]">ALVi</span>
                <span className="text-[#0A0A0A]">Posts</span>
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("about")} className="text-sm font-medium text-gray-600 hover:text-[#D00000] transition-colors" data-testid="nav-about">About</button>
              <button onClick={() => scrollToSection("services")} className="text-sm font-medium text-gray-600 hover:text-[#D00000] transition-colors" data-testid="nav-services">Services</button>
              <button onClick={() => scrollToSection("portfolio")} className="text-sm font-medium text-gray-600 hover:text-[#D00000] transition-colors" data-testid="nav-portfolio">Work</button>
              <button onClick={() => scrollToSection("process")} className="text-sm font-medium text-gray-600 hover:text-[#D00000] transition-colors" data-testid="nav-process">Process</button>
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-[#D00000] hover:bg-[#0A0A0A] text-white rounded-none px-6 py-2 font-bold uppercase tracking-wider text-xs transition-all duration-300"
                data-testid="nav-cta">

                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle">

              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="md:hidden bg-white border-t border-gray-200 py-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4 px-6">
              <button onClick={() => scrollToSection("about")} className="text-left py-2 text-gray-600 hover:text-[#D00000]">About</button>
              <button onClick={() => scrollToSection("services")} className="text-left py-2 text-gray-600 hover:text-[#D00000]">Services</button>
              <button onClick={() => scrollToSection("portfolio")} className="text-left py-2 text-gray-600 hover:text-[#D00000]">Work</button>
              <button onClick={() => scrollToSection("process")} className="text-left py-2 text-gray-600 hover:text-[#D00000]">Process</button>
              <Button
              onClick={() => scrollToSection("contact")}
              className="bg-[#D00000] hover:bg-[#0A0A0A] text-white rounded-none w-full py-3 font-bold uppercase tracking-wider text-xs">

                Get Started
              </Button>
            </div>
          </div>
        }
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-20 md:pt-0 bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-0">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center min-h-[calc(100vh-80px)]">
            {/* Left Content */}
            <div className="md:col-span-7 animate-fade-in-up">
              <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">Digital Marketing Agency</p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl uppercase tracking-tighter leading-none text-[#0A0A0A] mb-6">
                DIGITAL<br />
                <span className="text-[#D00000]">GROWTH</span><br />
                FOR LOCAL<br />
                BUSINESS
              </h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-xl mb-8">
                We help local businesses build a powerful online presence. From professional websites to targeted social media ads — we handle your digital marketing so you can focus on what you do best.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => scrollToSection("contact")}
                  className="bg-[#D00000] hover:bg-[#0A0A0A] text-white rounded-none px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all duration-300 flex items-center gap-2"
                  data-testid="hero-cta">

                  Start Growing Today <ArrowRight size={18} />
                </Button>
                <Button
                  onClick={() => scrollToSection("portfolio")}
                  variant="outline"
                  className="border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white rounded-none px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all duration-300"
                  data-testid="hero-secondary-cta">

                  See Our Work
                </Button>
              </div>
            </div>

            {/* Right Side - Digital Marketing Visual */}
            <div className="md:col-span-5 hidden md:block animate-fade-in animation-delay-300">
              <div className="relative bg-[#0A0A0A] p-8 md:p-12">
                <div className="grid grid-cols-2 gap-6">
                  {/* Website Icon */}
                  <div className="bg-[#D00000] p-6 flex flex-col items-center justify-center aspect-square">
                    <Globe className="text-white mb-3" size={48} strokeWidth={1.5} />
                    <p className="text-white text-xs font-bold uppercase tracking-wider text-center">Websites</p>
                  </div>
                  {/* Social Media Icon */}
                  <div className="bg-white p-6 flex flex-col items-center justify-center aspect-square">
                    <Megaphone className="text-[#0A0A0A] mb-3" size={48} strokeWidth={1.5} />
                    <p className="text-[#0A0A0A] text-xs font-bold uppercase tracking-wider text-center">Social Ads</p>
                  </div>
                  {/* Analytics Icon */}
                  <div className="bg-white p-6 flex flex-col items-center justify-center aspect-square">
                    <BarChart3 className="text-[#D00000] mb-3" size={48} strokeWidth={1.5} />
                    <p className="text-[#0A0A0A] text-xs font-bold uppercase tracking-wider text-center">Analytics</p>
                  </div>
                  {/* Growth Icon */}
                  <div className="bg-[#D00000] p-6 flex flex-col items-center justify-center aspect-square">
                    <TrendingUp className="text-white mb-3" size={48} strokeWidth={1.5} />
                    <p className="text-white text-xs font-bold uppercase tracking-wider text-center">Growth</p>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-[#D00000]"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0A0A0A] py-12 md:py-16" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center stat-item">
              <p className="font-heading text-4xl md:text-5xl text-white mb-2">100%</p>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Client Satisfaction</p>
            </div>
            <div className="text-center stat-item animation-delay-100">
              <p className="font-heading text-4xl md:text-5xl text-[#D00000] mb-2">24hr</p>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Response Time</p>
            </div>
            <div className="text-center stat-item animation-delay-200">
              <p className="font-heading text-4xl md:text-5xl text-white mb-2">Local</p>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Business Focus</p>
            </div>
            <div className="text-center stat-item animation-delay-300">
              <p className="font-heading text-4xl md:text-5xl text-[#D00000] mb-2">ROI</p>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Driven Results</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="w-full aspect-[4/5] bg-[#0A0A0A] flex items-center justify-center">
                  <div className="text-center p-8">
                    <p className="font-heading text-5xl md:text-6xl tracking-tight">
                      <span className="text-[#D00000]">ALVi</span><span className="text-white">Posts</span>
                    </p>
                    <div className="w-16 h-1 bg-[#D00000] mx-auto mt-6 mb-4"></div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Digital Marketing</p>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Agency</p>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 border-4 border-[#D00000]"></div>
              </div>
            </div>
            <div className="md:col-span-7">
              <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">About Us</p>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight text-[#0A0A0A] mb-6">
                YOUR DIGITAL<br />GROWTH PARTNER
              </h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                ALViPosts is a digital marketing agency dedicated to helping local businesses thrive in the digital age. We understand that many business owners are experts at their craft but may not have the time or expertise to build an online presence.
              </p>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">
                That's where we come in. We create professional websites and run targeted social media advertising campaigns that bring real customers through your door. No jargon, no complicated contracts — just results-driven marketing that grows your business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#D00000] flex-shrink-0" size={20} />
                  <span className="text-sm font-medium">Professional Websites</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#D00000] flex-shrink-0" size={20} />
                  <span className="text-sm font-medium">Targeted Advertising</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#D00000] flex-shrink-0" size={20} />
                  <span className="text-sm font-medium">Local Market Expertise</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-[#D00000] flex-shrink-0" size={20} />
                  <span className="text-sm font-medium">Transparent Pricing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 bg-[#F4F4F5]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">What We Do</p>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight text-[#0A0A0A]">
              OUR SERVICES
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) =>
            <div
              key={index}
              className="bg-white border border-gray-200 hover:border-[#D00000] transition-colors duration-300 p-8"
              data-testid={`service-card-${index}`}>

                <service.icon className="text-[#D00000] mb-6" size={40} strokeWidth={1.5} />
                <h3 className="font-heading text-xl uppercase tracking-tight text-[#0A0A0A] mb-4">{service.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-24 md:py-32 bg-white" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">Our Work</p>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight text-[#0A0A0A]">
              EXAMPLE PROJECTS
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Here are examples of the kind of work we deliver for local businesses like yours.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {portfolioItems.map((item) =>
            <div
              key={item.id}
              className="portfolio-card group bg-white border border-gray-200 hover:border-[#D00000] transition-all duration-300"
              data-testid={`portfolio-item-${item.id}`}>

                <div className="aspect-video overflow-hidden">
                  <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                </div>
                <div className="p-6 md:p-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-2">{item.category}</p>
                  <h3 className="font-heading text-2xl uppercase tracking-tight text-[#0A0A0A] mb-2">{item.title}</h3>
                  <p className="text-sm font-medium text-gray-800 mb-2">{item.description}</p>
                  <p className="text-sm text-gray-600">{item.details}</p>
                </div>
              </div>
            )}
          </div>
          <div className="text-center mt-12">
            <p className="text-sm text-gray-500 italic">* Example projects demonstrating our capabilities</p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">How It Works</p>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight text-white">
              OUR PROCESS
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) =>
            <div
              key={index}
              className="relative"
              data-testid={`process-step-${index}`}>

                <p className="font-heading text-6xl text-[#D00000] opacity-50 mb-4">{step.step}</p>
                <h3 className="font-heading text-xl uppercase tracking-tight text-white mb-3">{step.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{step.description}</p>
                {index < processSteps.length - 1 &&
              <div className="hidden lg:block absolute top-8 right-0 w-8">
                    <ArrowRight className="text-[#D00000]" size={24} />
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-[#D00000] mb-4">Get Started</p>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight text-[#0A0A0A] mb-6">
              LET'S GROW YOUR BUSINESS
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Ready to take your business online? Schedule a free discovery call or send us a message and we'll get back to you within 24 hours.
            </p>
          </div>

          {/* Contact Info Bar */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#D00000] flex items-center justify-center">
                <Phone className="text-white" size={18} />
              </div>
              <p className="font-medium text-[#0A0A0A]">(541) 530-9858</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0A0A0A] flex items-center justify-center">
                <Mail className="text-white" size={18} />
              </div>
              <p className="font-medium text-[#0A0A0A]">vincentalexander425@gmail.com</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                <MapPin className="text-[#D00000]" size={18} />
              </div>
              <p className="font-medium text-[#0A0A0A]">Serving Local Businesses Everywhere</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left - Calendly */}
            <div className="bg-[#0A0A0A] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="text-[#D00000]" size={24} />
                <h3 className="font-heading text-xl uppercase tracking-tight text-white">Schedule a Call</h3>
              </div>
              <div className="bg-white" style={{ minHeight: '650px' }}>
                <iframe
                  src="https://calendly.com/vincentalexander425?hide_landing_page_details=1&hide_gdpr_banner=1"
                  width="100%"
                  height="650"
                  frameBorder="0"
                  title="Schedule a call with ALViPosts"
                  data-testid="calendly-embed"
                ></iframe>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-[#F4F4F5] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <MessageSquare className="text-[#D00000]" size={24} />
                <h3 className="font-heading text-xl uppercase tracking-tight text-[#0A0A0A]">Send a Message</h3>
              </div>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="business_name" className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2 block">
                      Business Name *
                    </Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      type="text"
                      required
                      value={formData.business_name}
                      onChange={handleInputChange}
                      placeholder="Your Business Name"
                      className="rounded-none border-2 border-gray-200 focus:border-[#D00000] focus:ring-0 bg-white p-4 h-auto"
                      data-testid="input-business-name" />

                  </div>
                  <div>
                    <Label htmlFor="contact_name" className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2 block">
                      Your Name *
                    </Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      type="text"
                      required
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="rounded-none border-2 border-gray-200 focus:border-[#D00000] focus:ring-0 bg-white p-4 h-auto"
                      data-testid="input-contact-name" />

                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@business.com"
                      className="rounded-none border-2 border-gray-200 focus:border-[#D00000] focus:ring-0 bg-white p-4 h-auto"
                      data-testid="input-email" />

                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="rounded-none border-2 border-gray-200 focus:border-[#D00000] focus:ring-0 bg-white p-4 h-auto"
                      data-testid="input-phone" />

                  </div>
                  <div>
                    <Label htmlFor="message" className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-2 block">
                      Tell Us About Your Business
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="What services are you interested in? What challenges are you facing with your online presence?"
                      rows={4}
                      className="rounded-none border-2 border-gray-200 focus:border-[#D00000] focus:ring-0 bg-white p-4 resize-none"
                      data-testid="input-message" />

                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#D00000] hover:bg-[#0A0A0A] text-white rounded-none px-8 py-4 font-bold uppercase tracking-wider text-sm transition-all duration-300 h-auto disabled:opacity-50"
                    data-testid="submit-button">

                    {isSubmitting ? "Sending..." : "Get Your Free Consultation"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <span className="font-heading text-2xl tracking-tight">
                <span className="text-[#D00000]">ALVi</span>
                <span className="text-white">Posts</span>
              </span>
            </div>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} ALViPosts. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => scrollToSection("about")} className="text-sm text-gray-400 hover:text-white transition-colors">About</button>
              <button onClick={() => scrollToSection("services")} className="text-sm text-gray-400 hover:text-white transition-colors">Services</button>
              <button onClick={() => scrollToSection("contact")} className="text-sm text-gray-400 hover:text-white transition-colors">Contact</button>
            </div>
          </div>
        </div>
      </footer>
    </div>);

}